export interface ScheduledTask {
    // The ID of the scheduled task, which is the timeout ID returned by setTimeout
    id: number;
    description: string;
    // You can add more relevant properties here, like the callback function
}

export class TaskScheduler {
    private scheduledTasks: ScheduledTask[] = [];

    /**
     * Schedules a new task to be executed after a specified delay.
     * @param callback The function to execute after the delay.
     * @param delay The delay in milliseconds before the callback is executed.
     * @param description A description for the task (optional).
     * @returns The ID of the scheduled task, which can be used for cancellation.
     */
    scheduleTask(callback: TimerHandler, delay: number, description: string = 'no description'): number {
        const taskId = window.setTimeout(callback, delay);
        this.scheduledTasks.push({ id: taskId, description });
        console.log(`Task scheduled with ID: ${taskId} (${description})`);
        return taskId;
    }

    /**
     * Cancels a specific scheduled task by its ID.
     * @param taskId The ID of the task to cancel.
     * @returns True if the task was found and cancelled, false otherwise.
     */
    cancelTask(taskId: number): boolean {
        const index = this.scheduledTasks.findIndex(task => task.id === taskId);
        if (index !== -1) {
            clearTimeout(taskId);
            this.scheduledTasks.splice(index, 1);
            console.log(`Task with ID: ${taskId} cancelled.`);
            return true;
        } else {
            console.log(`Task with ID: ${taskId} not found.`);
            return false;
        }
    }

    /**
     * Cancels all currently scheduled tasks.
     * @returns The number of tasks that were cancelled.
     */
    cancelAllTasks(): number {
        let cancelledCount = 0;
        this.scheduledTasks.forEach(task => {
            clearTimeout(task.id);
            cancelledCount++;
            console.log(`Task with ID: ${task.id} (${task.description}) cancelled.`);
        });
        this.scheduledTasks = [];
        console.log(`Cancelled a total of ${cancelledCount} scheduled tasks.`);
        return cancelledCount;
    }

    /**
     * Gets a list of all currently scheduled tasks (for inspection).
     * @returns An array of ScheduledTask objects.
     */
    getScheduledTasks(): ScheduledTask[] {
        return [...this.scheduledTasks]; // Return a copy to prevent external modification
    }
}
